import { ModulePlaceholder } from '../../components/ModulePlaceholder';
import { Badge, Card } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Phase 2E note: Purchasing / Inventory / Expenses moved to their own
 * real page files (PurchasingPage.tsx, InventoryPage.tsx, ExpensesPage.tsx).
 *
 * Reports stays deferred (decision D) - it must NOT be implemented in this
 * phase, so it keeps the placeholder.
 */

export function ReportsPage() {
  return (
    <ModulePlaceholder
      icon="reports"
      title="Reports"
      description="Business reports across all branches or a single branch."
      bullets={[
        'Transactions by branch & period',
        'Employee accountability report',
        'Purchasing & inventory reports',
        'Exportable summaries',
      ]}
    />
  );
}

/* ---------------- Backup & Recovery (status only) ---------------- */

const VERIFIED_FACTS: { title: string; body: string }[] = [
  {
    title: 'Scheduled automatically',
    body: 'Daily dump at 02:00 Asia/Manila (cron on the server), plus an automatic pre-deploy dump before every migration.',
  },
  {
    title: 'Method & location',
    body: 'pg_dump -Fc written to /var/backups/cannery/ on the database host, root-only (mode 600).',
  },
  {
    title: 'Verified before trusting',
    body: 'The backup script inspects every dump with pg_restore --list before keeping it.',
  },
  {
    title: 'Retention: 7 daily + 4 weekly + 3 monthly',
    body: 'Protect-then-prune — retention is computed in full before anything is deleted; fixture-tested by deploy/tests/test-retention.sh.',
  },
  {
    title: 'Restore test PASSED (2026-10-05)',
    body: 'deploy/restore-verify.sh restored the latest dump into a scratch database and verified schema, triggers, counters and logins. The live database was never modified; rerun monthly.',
  },
  {
    title: 'Development data is excluded',
    body: 'Local .pgdata/ development databases are disposable and never part of production backups.',
  },
];

const UNAVAILABLE_ITEMS: { title: string; body: string }[] = [
  {
    title: 'Last successful backup: not shown',
    body: 'This app has no backup API, so the timestamp of the most recent dump cannot be verified here.',
  },
  {
    title: 'On-disk dump inventory & free space: not shown',
    body: 'Listing /var/backups/cannery and checking disk usage requires server access.',
  },
  {
    title: 'Offsite copy status: not verified',
    body: 'The offsite (rclone) copy is an optional hook that must be configured once on the server; its state is unknown to the app.',
  },
  {
    title: 'Restore readiness: only as fresh as the last verification',
    body: 'The recorded restore test was run on 2026-10-05. A newer dump is only proven restorable after rerunning deploy/restore-verify.sh.',
  },
  {
    title: 'No backup or restore controls here — by design',
    body: 'Backups run server-side on a schedule; restores are a documented manual procedure (docs/backups.md) executed by an administrator over SSH.',
  },
];

/**
 * Backup status page (decision C): STATUS ONLY.
 *
 * Everything under "verified" is quoted from docs/backups.md and the shipped
 * deploy scripts; nothing here is a live check, and no health, timestamp or
 * restore-readiness value is invented. Deliberately contains NO backup,
 * restore or destructive controls.
 */
export function BackupPage() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Backup and Recovery</h1>
        <p className="mt-1 text-sm text-slate-500">
          PostgreSQL backups already run on the server. This page reports the{' '}
          <span className="font-semibold text-slate-700">verified, documented</span> facts and marks
          clearly what the app cannot see. It cannot start or restore anything.
        </p>
        <Badge tone="indigo" className="mt-2">
          Server-side backups · status only
        </Badge>
      </header>

      <section aria-label="Verified backup facts" className="space-y-3">
        <h2 className="text-lg font-bold tracking-tight text-slate-900">
          Verified configuration and history
        </h2>
        <p className="text-sm text-slate-500">
          Source: <span className="font-mono text-xs">docs/backups.md</span>,{' '}
          <span className="font-mono text-xs">deploy/cannery-backup.sh</span> and{' '}
          <span className="font-mono text-xs">deploy/restore-verify.sh</span> in this repository —
          configuration and recorded results, not a live status check.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {VERIFIED_FACTS.map((fact) => (
            <Card key={fact.title} className="flex items-start gap-3 p-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <Icon name="check" className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">{fact.title}</p>
                <p className="mt-0.5 text-sm text-slate-500">{fact.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section aria-label="Unavailable backup information" className="space-y-3">
        <h2 className="text-lg font-bold tracking-tight text-slate-900">
          Not available inside the app
        </h2>
        <p className="text-sm text-slate-500">
          There is deliberately no backup API, so this page never guesses. Anything below needs
          server access (SSH) and is listed here only so the gap is explicit.
        </p>
        <Card className="divide-y divide-slate-100">
          {UNAVAILABLE_ITEMS.map((item) => (
            <div key={item.title} className="flex items-start gap-3 p-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                <Icon name="alert" className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-700">{item.title}</p>
                <p className="mt-0.5 text-sm text-slate-500">{item.body}</p>
              </div>
            </div>
          ))}
        </Card>
      </section>
    </div>
  );
}
